import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SiteFooter } from "@/components/SiteFooter";
import PageMeta from "@/components/PageMeta";
import LegalPageShell, { LegalSection } from "@/components/legal/LegalPageShell";

const Privacy = () => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <PageMeta
      title="Privacy Policy — GoToCoins"
      description="What GoToCoins collects: newsletter email signups, Google Analytics usage data, account details and the optional read-only Swyftx API connection stored in your browser."
      path="/privacy"
    />
    <main className="flex-1 container mx-auto px-4 py-10 max-w-3xl">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>

      <LegalPageShell
        icon={Lock}
        title="Privacy Policy"
        subtitle="What GoToCoins collects, why, and how to get it removed."
        lastUpdated="26 August 2026"
      >
        <Card className="border-warning/30 bg-warning/5 mb-8">
          <CardContent className="py-4">
            <p className="text-sm">
              <strong>Draft — pending legal review.</strong> This policy describes the data flows
              currently implemented on the Site. It has not been reviewed by an Australian legal
              practitioner or privacy specialist and should be reviewed against the Privacy Act 1988
              (Cth) and the Australian Privacy Principles before launch.
            </p>
          </CardContent>
        </Card>

        <LegalSection title="1. Who we are">
          <p>
            GoToCoins is an Australian crypto research site operated by an individual sole operator.
            Contact details are on the{" "}
            <Link to="/contact" className="underline underline-offset-2">contact page</Link>.
          </p>
        </LegalSection>

        <LegalSection title="2. What we actually collect">
          <p><strong>Newsletter signup.</strong> When you subscribe we store your email address, the
            time you subscribed, and a hashed form of your IP address (used only to rate-limit
            signups and block abuse). We do not store your raw IP with the subscription.</p>
          <p><strong>Analytics.</strong> The Site uses Google Analytics 4 (measurement ID
            G-GQ3WXLM46D). It sets cookies and collects pages viewed, referrer, approximate location
            derived from IP, device and browser type, and interaction events such as running a coin
            review, clicking an exchange link, opening the chat assistant, and article scroll depth.
            A random anonymous visitor identifier is stored in your browser to join these events
            across a session. We also record equivalent product events to our own internal
            operations dashboard.</p>
          <p><strong>Accounts.</strong> If you create an account, our backend authentication provider
            stores your email address, a hashed password or OAuth identifier, and sign-in timestamps.</p>
          <p><strong>Chat assistant.</strong> Messages you send to GoToBuddy are processed by a
            third-party AI provider to generate a response, and transcripts may be retained to
            monitor quality and abuse. Do not paste API keys, seed phrases or personal financial
            details into chat.</p>
          <p><strong>Contact form.</strong> Your name, email and message are stored so we can reply.</p>
        </LegalSection>

        <LegalSection title="3. The Swyftx read-only connection">
          <p>
            If you choose to connect a Swyftx account, you supply a read-only API key. That key is
            stored in your own browser's local storage on your device. It is transmitted only to
            our proxy function for the purpose of retrieving your balances from Swyftx, and is not
            written to our database or logs.
          </p>
          <p>
            Balance data returned is displayed to you and is not retained server-side. Clearing the
            connection in the app, or clearing your browser storage, removes the key from your
            device. You should also revoke the key in your Swyftx account. Only ever issue keys with
            read permissions — never withdrawal or trading permissions.
          </p>
        </LegalSection>

        <LegalSection title="4. What we don't collect">
          <p>
            We do not collect payment card details, government identifiers, wallet private keys, or
            seed phrases, and we never ask for exchange credentials with withdrawal rights. We do not
            sell personal information.
          </p>
        </LegalSection>

        <LegalSection title="5. Why we use it">
          <ul className="list-disc pl-5 space-y-1">
            <li>to send the daily newsletter you asked for;</li>
            <li>to understand which tools and articles are used, and improve them;</li>
            <li>to authenticate you and remember your preferences;</li>
            <li>to detect abuse, spam and rate-limit violations; and</li>
            <li>to respond to your enquiries.</li>
          </ul>
        </LegalSection>

        <LegalSection title="6. Who we share it with">
          <p>
            We use third-party processors to run the Site: our backend, database, authentication and
            edge-function host; Google Analytics; our email delivery provider; and AI model providers
            used for content and chat. Some of these process data outside Australia, including in the
            United States and the European Union. We share only what each service needs, and never
            sell or rent your data.
          </p>
        </LegalSection>

        <LegalSection title="7. Cookies and similar technologies">
          <p>
            The Site uses cookies and browser storage for authentication sessions, analytics, your
            anonymous visitor identifier, dismissed notices, and (if you use it) your Swyftx key. You
            can block or clear cookies in your browser, and can opt out of Google Analytics using
            Google's browser add-on; core parts of the Site will still work.
          </p>
        </LegalSection>

        <LegalSection title="8. Retention">
          <p>
            Newsletter subscriptions are kept until you unsubscribe. Analytics data is retained
            according to our Google Analytics retention setting. Contact messages are kept while we
            deal with your enquiry and for a reasonable period afterwards. Account data is kept until
            you ask us to delete the account.
          </p>
        </LegalSection>

        <LegalSection title="9. Security">
          <p>
            Data is held in managed cloud infrastructure with access controls and row-level security
            policies restricting who can read each table. No system is perfectly secure; you provide
            information at your own risk. If a data breach is likely to cause serious harm we will
            notify affected users and the OAIC as required by the Notifiable Data Breaches scheme.
          </p>
        </LegalSection>

        <LegalSection title="10. Your rights">
          <p>
            You can ask us to access, correct, or delete the personal information we hold about you,
            unsubscribe from emails at any time, and complain about how we handle your data. Contact
            us via the <Link to="/contact" className="underline underline-offset-2">contact page</Link>.
            If you are not satisfied with our response, you can complain to the Office of the
            Australian Information Commissioner at oaic.gov.au.
          </p>
        </LegalSection>

        <LegalSection title="11. Children">
          <p>The Site is not intended for people under 18 and we do not knowingly collect their data.</p>
        </LegalSection>

        <LegalSection title="12. Changes">
          <p>
            We will update this page and the "last updated" date when our data practices change.
          </p>
        </LegalSection>
      </LegalPageShell>
    </main>
    <SiteFooter />
  </div>
);

export default Privacy;
